import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import apiClient from "../../../services/apiClient";
import { InputField, MultiSelectField, SelectField } from "../Petowner/AddOwnerModal";
import { SelectChangeEvent } from "@mui/material";
import moment from "moment";
import axios from "axios";

export default function AddServiceProviderModal({ onClose, onSuccess, editData, mode = "add", }: {
    onClose: () => void; onSuccess?: () => void; editData?: any; mode?: "add" | "edit";
}) {

    const coreFields = [
        "firstName", "lastName", "gender", "mobile", "email",
        "username", "password", "confirmPassword",
    ];

    const vetFields = [
        "GSTIN", "license_no", "aadhar_no", "dob", "blood_group",
        "practising_since", "isd_code"
    ];

    const bankFields = ["bank_name", "bank_ifsc", "bank_account_number"];

    const fieldKeys = [...coreFields, ...vetFields, ...bankFields];

    const createInitialState = (keys: string[]) =>
        Object.fromEntries(keys.map((key) => [key, ""])) as Record<string, string | string[]>;

    const initialFormState = createInitialState(fieldKeys);

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState<Record<string, string>>({});
    type OptionBase = { label: string; value: string };
    const [bankOptions, setBankOptions] = useState<OptionBase[]>([]);

    const [ifscDetails, setIfscDetails] = useState<any>(null);
    const [ifscLoading, setIfscLoading] = useState(false);
    const [isBankVerified, setIsBankVerified] = useState(false);
    const [isBankDisabled, setIsBankDisabled] = useState(false);

    useEffect(() => {
        const masterConfigs = [
            { slug: "bank_name", setter: setBankOptions },
        ];

        const fetchMasters = async () => {
            try {
                const results = await Promise.all(
                    masterConfigs.map(async ({ slug, setter }) => {
                        const res: any = await apiClient(
                            `/v1/users/get-master-x?slug=${slug}&order_name=name&dir=`
                        );
                        const masters = res?.data?.masters ?? [];

                        const mapped = masters.map((m: any) => ({
                            value: m.name || "",
                            label: m.name || "Unnamed",
                        }));

                        setter(mapped.sort((a: any, b: any) => a.label.localeCompare(b.label)));
                        return { slug, count: mapped.length };
                    })
                );

                console.log("✅ Masters loaded:", results);
            } catch (err) {
                console.error("❌ Failed to fetch master data:", err);
            }
        };

        fetchMasters();
    }, []);




    const verifyIFSC = async () => {
        const ifsc = (formData.bank_ifsc as string)?.toUpperCase().trim();
        const bank = (formData.bank_name as string);

        if (!bank) return setErrors((p) => ({ ...p, bank_name: "Please select a bank first." }));
        if (!ifsc) return;

        setIfscLoading(true);
        try {
            const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
            if (!res.ok) throw new Error();

            const data = await res.json();
            const apiBank = (data.BANK || "").toLowerCase();
            const selected = bank.toLowerCase();

            if (!apiBank.includes(selected)) {
                setIfscDetails(null);
                setErrors((p) => ({
                    ...p,
                    bank_ifsc: `The IFSC belongs to ${data.BANK}, not ${bank}.`,
                }));
                return setIsBankVerified(false), setIsBankDisabled(false);
            }

            // ✅ Verified successfully
            setIfscDetails(data);
            setErrors((p) => ({ ...p, bank_ifsc: "" }));
            setIsBankVerified(true);
            setIsBankDisabled(true);
        } catch {
            setIfscDetails(null);
            setIsBankVerified(false);
            setIsBankDisabled(false);
            setErrors((p) => ({ ...p, bank_ifsc: "Invalid or unrecognized IFSC code." }));
        } finally {
            setIfscLoading(false);
        }
    };

    useEffect(() => {
        if (mode !== "edit" || !editData) return;
        console.log("🟢 EDIT DATA (raw):", JSON.stringify(editData, null, 2));
        const user = editData.user || {};
        const info = editData.additional_info || {};

        const capitalizeFirst = (str: string) =>
            str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

        setFormData((prev) => ({
            ...prev,
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            email: user.email || "",
            mobile: user.mobile || "",
            username: user.username || "",
            gender: capitalizeFirst(user.gender || ""), // ✅ Capitalize gender
            dob: user.dob || "",
            blood_group: user.blood_group || "",
            license_no: editData.license_no || info.license_no || "",
            GSTIN: info.GSTIN || "",
            aadhar_no: info.aadhar_no || "",
            practising_since: info.practising_since || "",
            bank_account_number: info.bank_account_number || "",
            bank_name: info.bank_name || "",
            bank_ifsc: info.bank_ifsc || "",
            isd_code: user.isd_code || "+91",
        }));
    }, [editData, mode]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | SelectChangeEvent<string | number>
    ) => {
        const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;

        setFormData((p) => ({ ...p, [name]: value }));
        setErrors((p) => ({ ...p, [name]: "" }));

        if (name === "bank_ifsc" && !value.trim()) {
            setIsBankDisabled(false);
            setIsBankVerified(false);
            setIfscDetails(null);
        }
    };
    const resetForm = (close = false) => {
        setFormData(initialFormState);
        setErrors({});
        if (close) onClose();
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};
        const addOnlyFields = ["password", "confirmPassword", "bank_name", "bank_ifsc", "bank_account_number"];
        const baseRequiredFields = [
            "firstName",
            "lastName",
            "username",
            "GSTIN",
            "license_no",
            "aadhar_no",
            "dob",
            // "blood_group",
            "practising_since",
        ];

        // 🔹 Skip add-only fields in edit mode
        const requiredFields =
            mode === "edit"
                ? baseRequiredFields
                : [...baseRequiredFields, ...addOnlyFields];

        // 🧩 Check empties
        requiredFields.forEach((key) => {
            const value = formData[key];
            if (
                (typeof value === "string" && !value.trim()) ||
                (Array.isArray(value) && !value.length)
            ) {
                newErrors[key] = `${key.replace(/_/g, " ")} is required.`;
            }
        });

        // 🧠 Validators
        const validators: Record<string, (val: any) => string | null> = {
            gender: (v) => (!v ? "Please select gender." : null),
            mobile: (v) =>
                !v
                    ? "Mobile number is required."
                    : !/^[6-9]\d{9}$/.test(v)
                        ? "Enter valid 10-digit mobile number."
                        : null,
            email: (v) =>
                !v
                    ? "Email is required."
                    : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
                        ? "Enter valid email address."
                        : null,
            aadhar_no: (v) =>
                v && !/^\d{12}$/.test(v)
                    ? "Enter valid 12-digit Aadhar number."
                    : null,
            license_no: (v) =>
                v && !/^[A-Z0-9-]{4,20}$/i.test(v)
                    ? "Enter valid license number (4–20 alphanumeric)."
                    : null,
            GSTIN: (v) =>
                !v
                    ? "GSTIN is required."
                    : !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(v)
                        ? "Enter valid 15-character GSTIN (e.g., 22AAAAA0000A1Z5)."
                        : null,
            dob: (v) => {
                if (!v) return "Date of Birth is required.";
                const dob = new Date(v);
                if (isNaN(dob.getTime())) return "Enter valid Date of Birth.";
                const age = new Date().getFullYear() - dob.getFullYear();
                return age < 18 ? "Age must be 18 years or above." : null;
            },
            practising_since: (v) => {
                if (!v) return "Practising since is required.";
                const since = new Date(v);
                return isNaN(since.getTime()) ? "Enter valid practising date." : null;
            },
        };

        // ✅ Only include bank validations when adding
        if (mode === "add") {
            validators.bank_ifsc = (v) =>
                !v
                    ? "Bank IFSC is required."
                    : !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(v)
                        ? "Enter valid IFSC code (e.g., HDFC0001234)."
                        : !isBankVerified
                            ? "Please verify your IFSC before saving."
                            : null;

            validators.bank_account_number = (v) =>
                !v
                    ? "Bank account number is required."
                    : !/^\d{9,18}$/.test(v)
                        ? "Enter valid bank account number (9–18 digits)."
                        : null;
        }

        // Apply all validators
        Object.entries(validators).forEach(([key, fn]) => {
            const error = fn(formData[key]);
            if (error) newErrors[key] = error;
        });

        // 🔐 Password check only for Add mode
        if (mode === "add") {
            const password = formData.password || "";
            const confirmPassword = formData.confirmPassword || "";

            if (typeof password !== "string" || !password.trim()) {
                newErrors.password = "Password is required.";
            } else if (
                !/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/.test(password)
            ) {
                newErrors.password =
                    "Password must be 8+ chars, include uppercase, lowercase, number & special char.";
            }

            if (password !== confirmPassword) {
                newErrors.confirmPassword = "Passwords do not match.";
            }

            // IFSC-bank name mismatch validation (only Add)
            if (isBankVerified && ifscDetails?.BANK) {
                const selected =
                    Array.isArray(formData.bank_name)
                        ? formData.bank_name.join(" ").toLowerCase()
                        : (formData.bank_name || "").toLowerCase();

                const verified = (ifscDetails.BANK || "").toLowerCase();
                if (selected && !verified.includes(selected)) {
                    newErrors.bank_name = `Selected bank doesn't match verified IFSC (${ifscDetails.BANK}).`;
                }
            }
        }

        setErrors(newErrors);
        console.log("🔍 Validation Errors:", newErrors);
        return Object.keys(newErrors).length === 0;
    };



    const handleSave = async () => {
        console.log("🚀 Starting save process...");
        if (!validate()) return;

        try {
            const isEdit = mode === "edit";
            console.log("📝 Mode:", isEdit ? "Edit" : "Add");

            const formattedDob = formData.dob ? moment(formData.dob).format("YYYY-MM-DD") : "";
            const formattedPractisingSince = formData.practising_since
                ? moment(formData.practising_since).format("YYYY-MM-DD")
                : "";

            console.log("📅 DOB:", formattedDob);
            console.log("📅 Practising Since:", formattedPractisingSince);

            const formatGender = (g: string | string[]) => {
                const val = Array.isArray(g) ? g[0] : g;
                return val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : "";
            };

            const selectedBankFullName =
                bankOptions.find((b) => b.value === formData.bank_name)?.label || "";
            console.log("🏦 Selected Bank:", selectedBankFullName);

            // 🔹 Fetch existing provider info when editing
            let existingAdditionalInfo: any = {};
            if (isEdit && editData?.id) {
                try {
                    console.log("🔍 Fetching existing info for ID:", editData.id);
                    const res: any = await apiClient(`/v1/resource/service_provider/${editData.id}`, {
                        method: "GET",
                        headers: { "x-api-key": "bXaljR3Sd1Jy4oG0QGOiLRwIEpCzR13r" },
                    });

                    console.log("🔍 Full API Response:", JSON.stringify(res, null, 2));

                    // Try multiple paths to get additional_info
                    const serviceProviderData = res?.data?.data?.service_providers || res?.data?.service_providers || res?.service_providers;

                    console.log("🔍 Service Provider Data:", JSON.stringify(serviceProviderData, null, 2));

                    existingAdditionalInfo = serviceProviderData?.additional_info || {};

                    if (typeof existingAdditionalInfo === "string") {
                        existingAdditionalInfo = JSON.parse(existingAdditionalInfo);
                    }

                    console.log("📂 Existing Additional Info (RAW):", JSON.stringify(existingAdditionalInfo, null, 2));
                    console.log("📂 Service_location exists?", !!existingAdditionalInfo.Service_location);
                    console.log("📂 aadhar_image exists?", !!existingAdditionalInfo.aadhar_image);
                    console.log("📂 Business_hours exists?", !!existingAdditionalInfo.Business_hours);

                } catch (err) {
                    console.error("❌ Failed to fetch existing additional info:", err);
                    existingAdditionalInfo = {};
                }
            }

            // ✅ Start with ALL existing data (this preserves everything)
            let additionalInfo: any = isEdit ? { ...existingAdditionalInfo } : {};

            console.log("📋 additionalInfo AFTER spread (before updates):", JSON.stringify(additionalInfo, null, 2));

            // ✅ Update ONLY the fields that were edited in the form
            if (formData.GSTIN) {
                additionalInfo.GSTIN = formData.GSTIN;
            }
            if (formData.aadhar_no) {
                additionalInfo.aadhar_no = formData.aadhar_no;
            }
            if (formattedPractisingSince) {
                additionalInfo.practising_since = formattedPractisingSince;
            }
            if (formData.bank_account_number) {
                additionalInfo.bank_account_number = formData.bank_account_number;
            }
            if (selectedBankFullName) {
                additionalInfo.bank_name = selectedBankFullName;
            }
            if (typeof formData.bank_ifsc === "string" && formData.bank_ifsc) {
                additionalInfo.bank_ifsc = formData.bank_ifsc.toUpperCase();
            }
            if (formData.license_no) {
                additionalInfo.license_no = formData.license_no;
            }

            // ✅ Handle file uploads - ONLY update if new files were uploaded
            if (formData.avatar_url) {
                additionalInfo.avatar_url = formData.avatar_url;
            }
            if (formData.aadhar_url) {
                additionalInfo.aadhar_image = formData.aadhar_url;
            }
            if (formData.license_url) {
                additionalInfo.license_url = formData.license_url;
            }

            console.log("🧩 Final additional_info (AFTER updates):", JSON.stringify(additionalInfo, null, 2));
            console.log("🧩 Service_location in final?", !!additionalInfo.Service_location);
            console.log("🧩 aadhar_image in final?", !!additionalInfo.aadhar_image);
            console.log("🧩 Business_hours in final?", !!additionalInfo.Business_hours);

            // 🧍 User payload
            const userPayload: any = {
                first_name: formData.firstName || "",
                last_name: formData.lastName || "",
                email: formData.email || "",
                isd_code: formData.isd_code || "+91",
                mobile: formData.mobile || "",
                username: formData.username || "",
                gender: formatGender(formData.gender),
                dob: formattedDob || "",
                timezone_id: 1,
                blood_group: formData.blood_group || "",
                role: "service_provider",
                is_active: 1,
                is_2fa: 0,
            };

            if (!isEdit && formData.password) {
                userPayload.password = formData.password;
            }

            console.log("👤 User Payload:", JSON.stringify(userPayload, null, 2));

            const payload = {
                id: isEdit ? editData.id : undefined,
                user_id: isEdit ? editData.user_id || editData.user?.id : undefined,
                additional_info: additionalInfo,
                license_no: formData.license_no || "",
                user: userPayload,
            };

            console.log("📦 Final Payload to Send:", JSON.stringify(payload, null, 2));

            let response: any;
            if (isEdit) {
                console.log("📡 Sending PATCH request...");
                response = await apiClient(`/v1/resource/ServiceProvider/${editData.id}`, {
                    method: "PATCH",
                    body: payload,
                });
            } else {
                console.log("📡 Sending POST request...");
                response = await axios.post(`https://v3.televet-api.a2zhealth.in/v1/users/register`, payload, {
                    headers: {
                        "Content-Type": "application/json",
                        "x-api-key": "bXaljR3Sd1Jy4oG0QGOiLRwIEpCzR13r",
                    },
                });
            }

            console.log("✅ API Response:", response);

            if (
                response?.status === 200 ||
                response?.status === 201 ||
                response?.code === 200 ||
                response?.code === 201
            ) {
                alert(isEdit
                    ? "Service Provider updated successfully!"
                    : "Service Provider registered successfully!");
                resetForm(true);
                onSuccess?.();
            } else {
                throw new Error("Unexpected API response");
            }
        } catch (error: any) {
            console.error("❌ handleSave error:", error);
            alert(error?.response?.data?.message || error?.message || "Something went wrong.");
        }
    };


    const makeField = (name: string, extras: any = {}) => ({
        label: name
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim(),
        name,
        type: "text",
        ...extras,
    });


    const fields = [
        makeField("firstName"),
        makeField("lastName"),
        makeField("dob", { type: "date" }),
        makeField("gender", { type: "select", options: ["Male", "Female", "Other"] }),
        makeField("isd_code", {
            type: "select",
            options: [
                { label: "+91 (India)", value: "+91" },
                { label: "+1 (USA)", value: "+1" },
                { label: "+44 (UK)", value: "+44" },
                { label: "+61 (Australia)", value: "+61" },
                { label: "+971 (UAE)", value: "+971" },
                { label: "+81 (Japan)", value: "+81" },
            ],
        }),

        makeField("mobile", { type: "tel", placeholder: "Enter mobile number" }),
        makeField("email", { type: "email", placeholder: "Enter email address" }),
        makeField("username"),
        makeField("password", { isPassword: true }),
        makeField("confirmPassword", { isPassword: true }),
        makeField("practising_since", { type: "date" }),
        makeField("license_no"),
        makeField("aadhar_no"),
        makeField("GSTIN"),
        // makeField("blood_group", {
        //     type: "select",
        //     options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
        // }),



        makeField("bank_name", { type: "select", options: bankOptions }),
        makeField("bank_account_number", {
            placeholder: "Enter account number",
        }),
        makeField("bank_ifsc", {
            placeholder: "Enter IFSC code (e.g. HDFC0001234)",
        }),
    ];


    const actions = [
        { label: "Cancel", onClick: () => resetForm(true), color: "red" },
        { label: mode === "edit" ? "Update" : "Save", onClick: handleSave, color: "green" },
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto shadow-lg mx-auto">
                <h2 className="text-xl font-semibold mb-6 text-center border-b pb-2 text-gray-800">
                    {mode === "edit" ? "Edit Service Provider" : "Add Service Provider"}
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {fields.map((f) => {
                        if (mode === "edit" && (f.name === "password" || f.name === "confirmPassword" || f.name === "bank_name" || f.name === "bank_ifsc" ||
                            f.name === "bank_account_number")) {
                            return null;
                        }
                        const value = Array.isArray(formData[f.name])
                            ? (formData[f.name] as string[])[0] ?? ""
                            : (formData[f.name] as string) ?? "";

                        const commonProps = {
                            key: f.name,
                            ...f,
                            value,
                            error: errors[f.name],
                            onChange: handleChange,
                        };

                        if (f.type === "select")
                            return (
                                <SelectField
                                    {...commonProps}
                                    disabled={f.name === "bank_name" && isBankDisabled}
                                />
                            );

                        if (f.type === "multiselect")
                            return (
                                <MultiSelectField
                                    {...commonProps}
                                    options={(f.options || []).map((opt: any) =>
                                        typeof opt === "string" ? { label: opt, value: opt } : opt
                                    )}
                                    value={(formData[f.name] as string[]) || []}
                                    onChange={(name, values) =>
                                        setFormData((p) => ({ ...p, [name]: values }))
                                    }
                                />
                            );

                        if (f.name === "bank_ifsc")
                            return (
                                <div key={f.name} className="flex flex-col md:col-span-3">
                                    <label className="text-sm font-medium text-gray-700 mb-1">
                                        {f.label}
                                    </label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <input
                                            {...f}
                                            type="text"
                                            value={value}
                                            onChange={handleChange}
                                            disabled={isBankVerified}
                                            className={`flex-1 min-w-[200px] border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 ${errors[f.name]
                                                ? "border-red-500 focus:ring-red-400"
                                                : "border-gray-300 focus:ring-blue-400"
                                                }`}
                                        />
                                        <button
                                            type="button"
                                            onClick={verifyIFSC}
                                            disabled={!formData.bank_ifsc || isBankVerified || ifscLoading}
                                            className={`px-3 py-2 rounded-md text-sm font-medium ${isBankVerified
                                                ? "bg-green-600 text-white cursor-not-allowed"
                                                : "bg-blue-600 text-white hover:bg-blue-700"
                                                }`}
                                        >
                                            {ifscLoading
                                                ? "Verifying..."
                                                : isBankVerified
                                                    ? "Verified"
                                                    : "Verify"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData((p) => ({ ...p, bank_ifsc: "" }));
                                                setIsBankDisabled(false);
                                                setIsBankVerified(false);
                                                setIfscDetails(null);
                                            }}
                                            className="px-3 py-2 rounded-md text-sm font-medium bg-gray-300 hover:bg-gray-400 text-gray-800"
                                        >
                                            Clear
                                        </button>
                                    </div>
                                    {errors[f.name] && (
                                        <p className="text-xs text-red-600 mt-1">{errors[f.name]}</p>
                                    )}
                                </div>
                            );

                        return <InputField {...commonProps} />;
                    })}
                </div>

                {ifscDetails && isBankVerified && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                        ✅ Verified {ifscDetails.BANK} – {ifscDetails.BRANCH}, {ifscDetails.ADDRESS}
                    </div>
                )}

                {ifscLoading && <p className="text-gray-500 mt-2 text-sm">Verifying IFSC...</p>}
                <div className="border-t pt-4 mt-6 flex justify-center gap-3">
                    {actions.map(({ label, onClick, color }) => (
                        <button
                            key={label}
                            onClick={() => {
                                console.log(`🖱️ Button clicked: ${label}`);
                                onClick?.();
                            }}
                            className={`px-5 py-2 bg-${color}-600 text-white rounded-md text-sm font-medium hover:bg-${color}-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                            disabled={label === "Save" && !isBankVerified}
                        >
                            {label}
                        </button>

                    ))}
                </div>
            </div>
        </div>
    );
}